import {AFrameScene, DISABLE_IMAGE_TARGETS} from '../lib/aframe-components'
import {FloatingBackButton} from '../lib/material-ui-components'
import {createSceneHtml, createSceneHtmlAsync, getModelInfo} from './model-config'
import {
  experienceConfigs, 
  getAllExperiencesFromWordPress, 
  testWordPressConnection
} from './experience-navigator'
import {generateAllARExperiences} from './ar-experiences-config'
import {
  EXPERIENCES,
  getExperienceConfig as getQRExperienceConfig
} from '../lib/experience-routes'
import './main.css'

const {withRouter} = ReactRouterDOM

// Componente universale per tutte le esperienze 3D
const Universal3DExperience = withRouter(({location, history}) => {
  // Controlla se c'è un parametro exp nell'URL
  const urlParams = new URLSearchParams(location.search)
  const expParam = urlParams.get('exp')
  
  // Usa React hooks sempre, non condizionalmente - TUTTI I HOOKS DEVONO ESSERE QUI PRIMA DI QUALSIASI RETURN
  const [experienceFound, setExperienceFound] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(!!expParam) // Solo se c'è expParam
  const [finalState, setFinalState] = React.useState(location.state || {})
  const [sceneHtml, setSceneHtml] = React.useState(null)
  const [sceneReady, setSceneReady] = React.useState(false)
  const renderCountRef = React.useRef(0)
  renderCountRef.current += 1

  // ⚠️ IMPORTANT: estrai subito i parametri da finalState.
  // In caso contrario, l'uso di variabili (es. showScene) prima di questa destructuring
  // può causare errori runtime tipo: "Cannot access 'E' before initialization" (TDZ).
  const {
    modelType = 'generic',
    customParams = {},
    showScene = false,
    title,
    description,
    experienceType,
  } = finalState || {}

  // Debug render context (helps identify the branch that triggers the crash)
  try {
    if (renderCountRef.current <= 10) {
      console.groupCollapsed(`🧩 [Universal3DExperience render #${renderCountRef.current}]`)
      console.log('pathname:', location?.pathname)
      console.log('search:', location?.search)
      console.log('has expParam:', !!expParam, 'expParam:', expParam)
      console.log('showScene:', showScene, 'sceneReady:', sceneReady)
      console.log('modelType:', modelType)
      console.log('finalState keys:', Object.keys(finalState || {}))
      console.log('finalState:', finalState)
      console.groupEnd()
    }
  } catch (e) {
    // never block rendering
  }
  
  // Effect per caricare l'esperienza se c'è il parametro exp
  React.useEffect(() => {
    try {
      if (!expParam) {
        setFinalState(location.state || {})
        return
      }
      
      console.log(`🎯 Parametro exp rilevato: ${expParam}`)
      
      const loadExperienceByID = async () => {
        setIsLoading(true)
        
        try {
          console.log(`🔍 Cercando esperienza con ID: ${expParam}`)
        
        // Prima prova a cercare nelle configurazioni QR (experience-routes.ts)
        let foundExperience = null
        
        console.log(`🔍 DEBUG: Cercando esperienza '${expParam}' in EXPERIENCES`)
        console.log(`🔍 DEBUG: EXPERIENCES keys disponibili:`, Object.keys(EXPERIENCES))
        console.log(`🔍 DEBUG: EXPERIENCES[${expParam}] exists:`, expParam in EXPERIENCES)
        console.log(`🔍 DEBUG: EXPERIENCES[${expParam}] content:`, EXPERIENCES[expParam])
        
        if (EXPERIENCES[expParam]) {
          console.log(`✅ Esperienza trovata nel sistema QR: ${expParam}`)
          
          // Estrai tutti i parametri dall'URL
          const urlParams = new URLSearchParams(location.search)
          const customParams = {}
          urlParams.forEach((value, key) => {
            if (key !== 'exp') {
              customParams[key] = value
            }
          })
          
          console.log(`🔧 Parametri personalizzati dal QR:`, customParams)
          
          // Usa getQRExperienceConfig per ottenere la configurazione completa
          const qrConfig = getQRExperienceConfig(expParam, customParams)
          
          console.log(`📋 Configurazione QR completa:`, qrConfig)
          
          // Crea la struttura compatibile con il sistema esistente
          foundExperience = {
            id: expParam,
            title: qrConfig.title,
            text: qrConfig.description,
            config: {
              experienceType: qrConfig.experienceType || '3d',
              modelType: qrConfig.modelType,
              title: qrConfig.title,
              description: qrConfig.description,
              customParams: {
                // Parametri di base
                scale: qrConfig.customParams.scale || qrConfig.defaultScale,
                position: qrConfig.customParams.position || qrConfig.defaultPosition,
                rotation: qrConfig.customParams.rotation || qrConfig.defaultRotation,
                // Parametri di animazione dai defaultCustomParams
                ...qrConfig.customParams
              }
            }
          }
          
          console.log(`🎯 Esperienza QR convertita:`, foundExperience)
          console.log(`🎭 QR Animation params:`, {
            animation: foundExperience.config.customParams.animation,
            repeat: foundExperience.config.customParams.repeat,
            listAnim: foundExperience.config.customParams.listAnim,
            text: foundExperience.config.customParams.text
          })
          
          // DEBUG: Stampa lista completa esperienze WordPress (config incluse) anche nel ramo QR
          try {
            console.log('🔎 DEBUG QR: provo a recuperare e stampare la lista completa delle esperienze WordPress...')
            const isWPOnlineForLog = await testWordPressConnection()
            if (isWPOnlineForLog) {
              const wpListForLog = await getAllExperiencesFromWordPress()
              const wpSummaryForLog = wpListForLog.map(exp => ({
                id: exp?.id,
                title: exp?.title,
                config: exp?.config
              }))
              console.log('📚 DEBUG QR: Lista completa esperienze WordPress (config inclusa):', wpSummaryForLog)
            } else {
              console.log('⚠️ DEBUG QR: WordPress offline, impossibile stampare la lista esperienze WordPress')
            }
          } catch (e) {
            console.warn('⚠️ DEBUG QR: errore nel recupero lista esperienze WordPress:', e?.message)
          }
          
        } else {
          // Fallback: cerca nelle altre configurazioni
          console.log(`🔍 Esperienza non trovata nel sistema QR, cerco nelle altre configurazioni...`)
          
          // Carica tutte le esperienze usando lo stesso sistema della home
          let allExperiences = []
          
          // 1. Prova a caricare da WordPress
          const isWordPressOnline = await testWordPressConnection()
          
          if (isWordPressOnline) {
            console.log('✅ WordPress online, carico esperienze (stessa logica della home)...')
            const wpExperiences = await getAllExperiencesFromWordPress()
            console.log(`📋 WordPress ha restituito ${wpExperiences.length} esperienze già convertite`);
            console.log(`📋 Prima esperienza esempio:`, wpExperiences[0] ? {
              id: wpExperiences[0].id,
              modelType: wpExperiences[0].config?.modelType,
              customParams: wpExperiences[0].config?.customParams
            } : 'Nessuna esperienza');
            // DEBUG COMPLETO: stampa tutta la lista con le configurazioni
            try {
              const wpSummary = wpExperiences.map(exp => ({
                id: exp?.id,
                title: exp?.title,
                config: exp?.config
              }))
              console.log('📚 Lista completa esperienze WordPress (config inclusa):', wpSummary)
            } catch (e) {
              console.warn('⚠️ Impossibile stampare la lista completa delle esperienze WordPress:', e?.message)
            }
            allExperiences = [...allExperiences, ...wpExperiences]
          } else {
            console.log('⚠️ WordPress offline, uso esperienze di fallback...')
            allExperiences = [...allExperiences, ...Object.values(experienceConfigs)]
          }
          
          // 2. Aggiungi le esperienze AR generate dinamicamente
          const arExperiences = generateAllARExperiences()
          allExperiences = [...allExperiences, ...arExperiences]
          
          console.log(`📋 Esperienze alternative disponibili: ${allExperiences.length}`)
          console.log(`📋 IDs alternativi:`, allExperiences.map(exp => exp.id))
          // DEBUG COMPLETO: stampa tutta la lista alternativa con le configurazioni
          try {
            const allSummary = allExperiences.map(exp => ({
              id: exp?.id,
              title: exp?.title,
              config: exp?.config || {
                experienceType: exp?.experienceType,
                modelType: exp?.modelType,
                scale: exp?.scale,
                position: exp?.position,
                rotation: exp?.rotation,
                animation: exp?.animation,
                repeat: exp?.repeat,
                listAnim: exp?.listAnim,
                text: exp?.text,
                customParams: exp?.customParams
              }
            }))
            console.log('📚 Lista completa esperienze alternative (config inclusa):', allSummary)
          } catch (e) {
            console.warn('⚠️ Impossibile stampare la lista completa delle esperienze alternative:', e?.message)
          }
          
          // 3. Cerca l'esperienza per ID (supporta sia id numerico sia experience_id slug)
          foundExperience = allExperiences.find(exp => {
            const wpSlug = exp.experience_id || exp.config?.experienceId || exp.config?.customParams?.experienceId
            return exp.id === expParam || wpSlug === expParam
          })
          
          if (foundExperience) {
            console.log(`✅ Esperienza trovata nelle configurazioni alternative: ${expParam}`, foundExperience)
            
            // Se l'esperienza ha già una config (viene da WordPress), usala direttamente (come fa la home)
            if (foundExperience.config) {
              console.log(`✅ Esperienza ha già config da WordPress, uso diretto (come home)`);
              console.log(`📋 Config WordPress:`, {
                modelType: foundExperience.config.modelType,
                customParams: foundExperience.config.customParams
              });
            } else if (foundExperience.experienceType) {
              // Solo se è locale (da experienceConfigs) e non ha config, convertila
              console.log(`🔄 Conversione esperienza locale per QR: ${expParam}`);
              foundExperience = {
                id: foundExperience.id,
                title: foundExperience.title,
                text: foundExperience.description,
                config: {
                  experienceType: foundExperience.experienceType,
                  modelType: foundExperience.modelType,
                  title: foundExperience.title,
                  description: foundExperience.description,
                  customParams: {
                    scale: foundExperience.scale || '1 1 1',
                    position: foundExperience.position || '0 0 0',
                    rotation: foundExperience.rotation || '0 0 0',
                    animation: foundExperience.animation || 'no',
                    repeat: foundExperience.repeat || 'no',
                    listAnim: foundExperience.listAnim || ['Scene'],
                    text: foundExperience.text || 'Animazione',
                    modelPath: foundExperience.modelPath || foundExperience.customParams?.modelPath
                  }
                }
              };
              console.log(`✅ Esperienza locale convertita:`, foundExperience);
            }
          }
        }
        
                  if (foundExperience) {
            setExperienceFound(foundExperience)
            console.log(`🔧 Configurazione finale esperienza ${expParam}:`, foundExperience.config)
            console.log(`📋 CustomParams finali:`, foundExperience.config?.customParams)
            
            // PRIMA: normalizza modelType (problemi noti + undefined -> generic per 3d)
            const originalModelType = foundExperience.config.modelType;
            console.log('🎯 QR rilevato, modelType originale:', originalModelType);
            
            // Nessun filtro speciale: mantieni il template dichiarato
            if (originalModelType === 'generic') {
              console.log('✅ Esperienza già generic, mantengo template generic per parametri WordPress');
            } else {
              console.log('✅ Esperienza con template specifico, mantengo template originale:', originalModelType);
            }
            
            // Gestione modelType undefined dopo normalizzazione iniziale
            if (!foundExperience.config.modelType || foundExperience.config.modelType === 'undefined') {
              console.log('⚠️ ModelType undefined da WordPress, uso default in base al tipo esperienza...');
              
              if (foundExperience.config.experienceType === '3d') {
                foundExperience.config.modelType = 'generic';
                console.log('✅ ModelType impostato a "generic" per esperienza 3d');
              } else {
                // Per altri tipi di esperienza, cerca nelle configurazioni hardcoded
                const hardcodedConfig = experienceConfigs[expParam];
                if (hardcodedConfig && hardcodedConfig.modelType) {
                  console.log('✅ Configurazione hardcoded trovata:', hardcodedConfig.modelType);
                  
                  // Sostituisci la configurazione con quella hardcoded
                  foundExperience.config = {
                    ...foundExperience.config,
                    modelType: hardcodedConfig.modelType,
                    scale: hardcodedConfig.scale || foundExperience.config.scale,
                    position: hardcodedConfig.position || foundExperience.config.position,
                    rotation: hardcodedConfig.rotation || foundExperience.config.rotation,
                    animation: hardcodedConfig.animation || foundExperience.config.animation,
                    repeat: hardcodedConfig.repeat || foundExperience.config.repeat,
                    listAnim: hardcodedConfig.listAnim || foundExperience.config.listAnim,
                    text: hardcodedConfig.text || foundExperience.config.text,
                    customParams: {
                      ...foundExperience.config.customParams,
                      modelPath: hardcodedConfig.modelPath || foundExperience.config.customParams?.modelPath,
                      scale: hardcodedConfig.scale || foundExperience.config.customParams?.scale,
                      position: hardcodedConfig.position || foundExperience.config.customParams?.position,
                      rotation: hardcodedConfig.rotation || foundExperience.config.customParams?.rotation,
                      animation: hardcodedConfig.animation || foundExperience.config.customParams?.animation,
                      repeat: hardcodedConfig.repeat || foundExperience.config.customParams?.repeat,
                      listAnim: hardcodedConfig.listAnim || foundExperience.config.customParams?.listAnim,
                      text: hardcodedConfig.text || foundExperience.config.customParams?.text
                    }
                  };
                  
                  console.log('🔧 Configurazione aggiornata con parametri hardcoded:', foundExperience.config);
                }
              }
            }
            
            // DOPO: crea correctParams in base al modelType finale
            let correctParams;
            
            if (foundExperience.config.modelType === 'generic') {
              // ESPERIENZA GENERIC (da WordPress): unisci i campi di config al customParams, dando priorità a customParams
              // Così vengono mantenuti scale/position/rotation/text/listAnim/repeat/animation di WordPress
              const cfg = foundExperience.config || {};
              const wpCP = cfg.customParams || {};
              correctParams = {
                scale: wpCP.scale ?? cfg.scale,
                position: wpCP.position ?? cfg.position,
                rotation: wpCP.rotation ?? cfg.rotation,
                modelPath: wpCP.modelPath ?? cfg.modelPath,
                animation: wpCP.animation ?? cfg.animation,
                repeat: wpCP.repeat ?? cfg.repeat,
                listAnim: wpCP.listAnim ?? cfg.listAnim,
                text: wpCP.text ?? cfg.text,
                ...wpCP
              };
              console.log('🎯 ESPERIENZA GENERIC: merge config-level + customParams WordPress (no hardcoded)');

              // Allinea anche la config usata dall'UI (bottone animazione) con i parametri corretti
              foundExperience.config.customParams = {
                ...foundExperience.config.customParams,
                scale: correctParams.scale,
                position: correctParams.position,
                rotation: correctParams.rotation,
                modelPath: correctParams.modelPath,
                animation: correctParams.animation,
                repeat: correctParams.repeat,
                listAnim: correctParams.listAnim,
                text: correctParams.text
              };
              console.log('🧩 ESPERIENZA GENERIC: customParams sincronizzati per UI:', foundExperience.config.customParams);
            } else {
              // ESPERIENZA SPECIFICA: USA SEMPRE i valori di WordPress (config-level) in priorità
              // I customParams possono contenere defaults di template: NON devono sovrascrivere WordPress
              const cfg = foundExperience.config || {};
              const cp = cfg.customParams || {};
              correctParams = {
                scale: cfg.scale ?? cp.scale,
                position: cfg.position ?? cp.position,
                rotation: cfg.rotation ?? cp.rotation,
                modelPath: cfg.modelPath ?? cp.modelPath,
                animation: cfg.animation ?? cp.animation,
                repeat: cfg.repeat ?? cp.repeat,
                listAnim: cfg.listAnim ?? cp.listAnim,
                text: cfg.text ?? cp.text,
                ...cp
              };
              // Sincronizza anche nei customParams per la UI (bottone animazione)
              foundExperience.config.customParams = {
                ...foundExperience.config.customParams,
                scale: correctParams.scale,
                position: correctParams.position,
                rotation: correctParams.rotation,
                modelPath: correctParams.modelPath,
                animation: correctParams.animation,
                repeat: correctParams.repeat,
                listAnim: correctParams.listAnim,
                text: correctParams.text
              };
              console.log('🏰 ESPERIENZA SPECIFICA: precedenza ai parametri WordPress (config-level), customParams sincronizzati');
            }
            
            console.log('🔧 Parametri corretti originali (da QR):', correctParams);
            
            // DEBUG SPECIFICO per parametri di animazione
            console.log('🎭 DEBUG ANIMAZIONE QR - Verifica parametri:');
            console.log('  - animation da config:', foundExperience.config.animation);
            console.log('  - animation da customParams:', foundExperience.config.customParams?.animation);
            console.log('  - repeat da config:', foundExperience.config.repeat);
            console.log('  - repeat da customParams:', foundExperience.config.customParams?.repeat);
            console.log('  - listAnim da config:', foundExperience.config.listAnim);
            console.log('  - listAnim da customParams:', foundExperience.config.customParams?.listAnim);
            console.log('  - text da config:', foundExperience.config.text);
            console.log('  - text da customParams:', foundExperience.config.customParams?.text);
            console.log('  - PARAMETRI FINALI in correctParams:');
            console.log('    * animation:', correctParams.animation);
            console.log('    * repeat:', correctParams.repeat);
            console.log('    * listAnim:', correctParams.listAnim);
            console.log('    * text:', correctParams.text);
            console.log('  - MODELTYPE FINALE per QR:', foundExperience.config.modelType);
            
            // QR 3D MODEL/IFRAME: rimuovi qualsiasi completamento da hardcoded. Usa SOLO parametri WordPress
            console.log('✅ QR: niente merge con hardcoded. Uso solo parametri WordPress.');
            
            // Se è iframe non generare scena A-Frame, imposta solo lo stato
            if (foundExperience.config.experienceType === 'iframe') {
              setFinalState({
                ...foundExperience.config,
                showScene: false
              })
              setSceneHtml(null)
              setSceneReady(true)
              return
            }

            // Usa la stessa logica della home per 3D/AR/Ologrammi: genera scena
            console.log('🏠 Usando la stessa logica della navigazione home...');
            console.log('📋 foundExperience.config:', foundExperience.config);
            
            // Genera la scena usando createSceneHtmlAsync per permettere il recupero dei parametri da WordPress
            // anche quando si arriva da QR
            const generateSceneForQR = async () => {
              try {
                console.log('🎬 Generazione scena asincrona per QR con verifica WordPress...');
                
                // IMPORTANTE: Usa i parametri corretti originali, NON quelli modificati
                console.log('🔧 Usando parametri corretti originali (da QR):', correctParams);
                console.log('🎯 MODELTYPE FINALE per createSceneHtmlAsync:', foundExperience.config.modelType);
                console.log('🎭 CHIAMATA createSceneHtmlAsync con parametri animazione:', {
                  modelType: foundExperience.config.modelType,
                  animation: correctParams.animation,
                  repeat: correctParams.repeat,
                  listAnim: correctParams.listAnim,
                  text: correctParams.text,
                  experienceId: expParam
                });
                
                const asyncSceneHtml = await createSceneHtmlAsync(
                  foundExperience.config.modelType, 
                  correctParams, // Usa i parametri corretti originali, NON i customParams completati
                  expParam // Passa l'experienceId per permettere il recupero da WordPress
                );
                setSceneHtml(asyncSceneHtml);
                console.log('✅ Scena HTML generata asincronamente per QR');
              } catch (error) {
                console.error('❌ Errore generazione scena asincrona per QR:', error);
                // Fallback alla versione sincrona
                console.log('🔄 Fallback alla versione sincrona per QR...');
                
                // Anche qui usa i parametri corretti originali
                console.log('🔧 Fallback con parametri corretti originali:', correctParams);
                console.log('🎯 MODELTYPE FINALE per createSceneHtml (fallback):', foundExperience.config.modelType);
                
                const directSceneHtml = createSceneHtml(
                  foundExperience.config.modelType, 
                  correctParams // Usa i parametri corretti originali
                );
                setSceneHtml(directSceneHtml);
                console.log('✅ Scena HTML generata sincronamente (fallback QR)');
              }
            };
            
            generateSceneForQR();
            setSceneReady(true);
            
            setFinalState({
              ...foundExperience.config,
              showScene: true
            })
          } else {
          console.warn(`⚠️ Esperienza non trovata in nessuna configurazione: ${expParam}`)
          setExperienceFound(null)
        }
        
        } catch (error) {
          console.error('❌ Errore nel caricamento esperienze:', error)
          setExperienceFound(null)
        } finally {
          setIsLoading(false)
        }
      }
      
      loadExperienceByID()
    } catch (e) {
      console.error('🧨 [Universal3DExperience] crash inside QR effect', e, {
        expParam,
        location: {pathname: location?.pathname, search: location?.search},
        finalState,
      })
      throw e
    }
  }, [expParam])
  
  // Effetto per generare la scena per navigazione normale (non QR) con WordPress
  React.useEffect(() => {
    try {
      if (!expParam && showScene && !sceneReady) {
        console.log('🔄 Generazione scena per navigazione normale con verifica WordPress...', {
          modelType,
          hasCustomParams: !!customParams,
          customParamsKeys: Object.keys(customParams || {}),
        })
        
        const experienceId = finalState?.experienceId || finalState?.id
        
        // Per navigazione normale, prova comunque a ottenere parametri da WordPress se disponibili
        const generateSceneForNormalNavigation = async () => {
          try {
            if (experienceId) {
              console.log('🔍 Tentativo caricamento parametri WordPress per navigazione normale:', experienceId)
              const asyncSceneHtml = await createSceneHtmlAsync(modelType, customParams, experienceId)
              setSceneHtml(asyncSceneHtml)
            } else {
              // Nessun ID esperienza, usa generazione sincrona
              const syncSceneHtml = createSceneHtml(modelType, customParams)
              setSceneHtml(syncSceneHtml)
            }
          } catch (error) {
            console.error('❌ Errore generazione scena navigazione normale:', error)
            // Fallback sincrono
            const syncSceneHtml = createSceneHtml(modelType, customParams)
            setSceneHtml(syncSceneHtml)
          }
          setSceneReady(true)
        }
        
        generateSceneForNormalNavigation()
      }
    } catch (e) {
      console.error('🧨 [Universal3DExperience] crash inside NORMAL-NAV effect', e, {
        expParam,
        showScene,
        sceneReady,
        modelType,
        customParams,
        finalState,
      })
      throw e
    }
  }, [expParam, showScene, sceneReady, finalState])

  // UNICO Hook per animazioni - sempre chiamato, logica condizionale all'interno
  React.useEffect(() => {
    // Estrarre i parametri qui dentro per sicurezza
    const {
      modelType = 'generic',
      customParams = {},
      showScene = false
    } = finalState

    // Estrai i parametri di animazione se presenti
    const animationParams = {}
    if (customParams && customParams.animation) {
      animationParams.animation = customParams.animation
      animationParams.repeat = customParams.repeat
      animationParams.listAnim = customParams.listAnim
      animationParams.text = customParams.text
    }

    // Solo se showScene è true e ci sono animazioni
    if (!showScene || !animationParams.animation || animationParams.animation !== 'yes') {
      return
    }

    console.log('🎬 Inizializzazione bottone animazione in Universal3DExperience...')
    
    // Timer per configurare il bottone dopo che la scena è pronta
    const setupTimer = setTimeout(() => {
      const button = document.getElementById('nextbutton')
      if (!button) {
        console.log('⏳ Bottone animazione non trovato, probabilmente non necessario per questa esperienza')
        return
      }
      
      console.log('✅ Bottone animazione trovato:', button)
      
      // Cerca il modello 3D
      let model = document.querySelector('#model')
      if (!model) {
        model = document.querySelector('[gltf-model]')
      }
      if (!model) {
        model = document.querySelector('a-entity[gltf-model]')
      }
      
      if (!model) {
        console.log('ℹ️ Modello 3D non trovato, probabilmente esperienza AR')
        return
      }
      
      console.log('✅ Modello 3D trovato:', model)
      
      // Configurazione animazione
      const listAnim = Array.isArray(animationParams.listAnim) 
        ? animationParams.listAnim 
        : JSON.parse(animationParams.listAnim || '["Scene"]')
      const repeat = animationParams.repeat || 'no'
      
      console.log('📋 Configurazione animazione:', { listAnim, repeat })
      
      // Indice corrente dell'animazione
      let currentAnimIndex = 0
      
      // Configura il click del bottone
      button.onclick = () => {
        console.log('🖱️ Click bottone animazione!')
        
        const currentAnim = listAnim[currentAnimIndex % listAnim.length]
        console.log('🎬 Avvio animazione:', currentAnim)
        
        // 🎵 GESTIONE AUDIO PER ANIMAZIONI
        // Variabile per l'URL dell'audio basato sull'URL del modello GLB
        let audioUrl = null;
        
        // Rileva l'URL del modello GLB dal DOM
        const modelElement = document.querySelector('#model');
        const genericModelAsset = document.querySelector('#genericModel');
        let modelGlbUrl = null;
        
        if (genericModelAsset && genericModelAsset.getAttribute('src')) {
          modelGlbUrl = genericModelAsset.getAttribute('src');
        } else if (modelElement && modelElement.getAttribute('gltf-model')) {
          const gltfModelValue = modelElement.getAttribute('gltf-model');
          
          // gltfModelValue può essere:
          // - stringa (es. "#asset" o URL diretto)
          // - oggetto (multi-prop) (es. {src: "#asset"/URL, dracoDecoderPath: "..."})
          const srcValue =
            (typeof gltfModelValue === 'string')
              ? gltfModelValue
              : (gltfModelValue && typeof gltfModelValue === 'object' && gltfModelValue.src)
                ? gltfModelValue.src
                : null;

          if (typeof srcValue === 'string') {
            if (srcValue.startsWith('#')) {
              // È un riferimento a un asset, cerca l'asset
              const assetElement = document.querySelector(srcValue);
              if (assetElement) {
                modelGlbUrl = assetElement.getAttribute('src');
              }
            } else {
              // È un URL diretto
              modelGlbUrl = srcValue;
            }
          }
        }
        
        console.log('🔍 DEBUG AUDIO - Rilevamento modello GLB:', { 
          modelElement,
          genericModelAsset,
          modelGlbUrl
        });
        
        // Logica condizionale basata sull'URL del modello GLB
        if (modelGlbUrl && modelGlbUrl.toLowerCase().includes('trapan')) {
          audioUrl = 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/drill.mp3';
          console.log('🎵 DEBUG AUDIO - Audio selezionato per trapano (da GLB URL):', audioUrl);
        } else if (modelGlbUrl && modelGlbUrl.toLowerCase().includes('radiomarconi')) {
          audioUrl = 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/morse.mp3';
          console.log('🎵 DEBUG AUDIO - Audio selezionato per morse (da GLB URL):', audioUrl);
        } else if (modelGlbUrl && modelGlbUrl.toLowerCase().includes('stazioneradio')) {
          audioUrl = 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/morse.mp3';
          console.log('🎵 DEBUG AUDIO - Audio selezionato per morse (da GLB URL):', audioUrl);
        }else {
          console.log('🔇 DEBUG AUDIO - Nessun audio configurato per questo modello');
          console.log('🔍 DEBUG AUDIO - Modello GLB non corrispondente:', {
            modelGlbUrl: modelGlbUrl,
            containsTrapan: modelGlbUrl ? modelGlbUrl.toLowerCase().includes('trapan') : false,
            containsMorse: modelGlbUrl ? modelGlbUrl.toLowerCase().includes('morse') : false
          });
        }
        
        console.log('🎵 DEBUG AUDIO - AudioUrl finale:', audioUrl);
        
        try {
          // 🎵 RIPRODUZIONE AUDIO QUANDO INIZIA L'ANIMAZIONE
          console.log('🎵 DEBUG AUDIO - Controllo riproduzione audio, audioUrl:', audioUrl);
          if (audioUrl) {
            console.log('🎵 DEBUG AUDIO - Iniziando riproduzione audio...');
            try {
              // Crea un nuovo elemento audio se non esiste già
              let audioElement = document.getElementById('animationAudio');
              console.log('🔊 DEBUG AUDIO - Elemento audio esistente:', audioElement);
              
              if (!audioElement) {
                audioElement = document.createElement('audio');
                audioElement.id = 'animationAudio';
                audioElement.preload = 'auto';
                audioElement.crossOrigin = 'anonymous';
                audioElement.loop = true; // 🔄 IMPOSTA LOOP AUDIO
                document.body.appendChild(audioElement);
                console.log('🔊 DEBUG AUDIO - Elemento audio creato dinamicamente con loop:', audioElement);
              } else {
                console.log('🔊 DEBUG AUDIO - Usando elemento audio esistente');
                audioElement.loop = true; // 🔄 ASSICURA LOOP ANCHE PER ELEMENTO ESISTENTE
              }
              
              // Imposta la sorgente audio e riproduci
              console.log('🎵 DEBUG AUDIO - Impostando sorgente audio con loop:', audioUrl);
              audioElement.src = audioUrl;
              audioElement.currentTime = 0; // Riavvia dall'inizio
              
              console.log('🎵 DEBUG AUDIO - Tentativo di riproduzione...');
              audioElement.play().then(() => {
                console.log('🎵 DEBUG AUDIO - Audio riprodotto con successo!', {
                  url: audioUrl,
                  duration: audioElement.duration,
                  volume: audioElement.volume,
                  readyState: audioElement.readyState
                });
              }).catch(err => {
                console.error('❌ DEBUG AUDIO - Errore riproduzione audio:', {
                  error: err.message,
                  code: err.code,
                  name: err.name,
                  audioUrl: audioUrl,
                  elementState: {
                    src: audioElement.src,
                    readyState: audioElement.readyState,
                    networkState: audioElement.networkState,
                    error: audioElement.error
                  }
                });
              });
              
              // Eventi aggiuntivi per debug
              audioElement.addEventListener('loadstart', () => console.log('🎵 DEBUG AUDIO - loadstart'));
              audioElement.addEventListener('loadeddata', () => console.log('🎵 DEBUG AUDIO - loadeddata'));
              audioElement.addEventListener('canplay', () => console.log('🎵 DEBUG AUDIO - canplay'));
              audioElement.addEventListener('error', (e) => console.error('❌ DEBUG AUDIO - error event:', e));
              
            } catch (audioError) {
              console.error('❌ DEBUG AUDIO - Errore gestione audio:', {
                error: audioError.message,
                stack: audioError.stack,
                audioUrl: audioUrl
              });
            }
          } else {
            console.log('🔇 DEBUG AUDIO - Nessun audioUrl impostato, salto riproduzione');
          }
          
          // Effetto visivo del click
          button.style.backgroundColor = 'white'
          button.style.color = '#AD50FF'
          
          // Rimuovi animazione corrente
          if (model.hasAttribute('animation-mixer')) {
            model.removeAttribute('animation-mixer')
          }
          
          // Avvia nuova animazione
          setTimeout(() => {
            model.setAttribute('animation-mixer', {
              clip: currentAnim,
              loop: repeat === 'yes' ? 'repeat' : 'once',
              crossFadeDuration: 0.4,
            })
            console.log('✅ Animazione avviata!')
          }, 50)
          
          // Passa alla prossima animazione
          if (listAnim.length > 1) {
            currentAnimIndex = (currentAnimIndex + 1) % listAnim.length
          }
          
          // Ripristina colore bottone
          setTimeout(() => {
            button.style.backgroundColor = '#AD50FF'
            button.style.color = '#FFF'
          }, 150)
          
        } catch (error) {
          console.error('❌ Errore animazione:', error)
        }
      }
      
      console.log('✅ Bottone animazione configurato!')
      
    }, 2000) // Aspetta 2 secondi per essere sicuri che la scena sia caricata
    
    // Cleanup
    return () => {
      clearTimeout(setupTimer)
    }
    
  }, [finalState])

  
  // Se c'è expParam e stiamo ancora caricando, mostra loading
  if (expParam && isLoading) {
    return (
      <div style={{
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        fontSize: '18px'
      }}>
        <div style={{fontSize: '48px', marginBottom: '20px'}}>⏳</div>
        <div>Caricamento esperienza {expParam}...</div>
      </div>
    )
  }
  
  // Se c'è expParam ma esperienza non trovata, mostra errore
  if (expParam && !isLoading && experienceFound === null) {
    return (
      <div style={{
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        fontSize: '18px',
        textAlign: 'center',
        padding: '20px'
      }}>
        <div style={{fontSize: '48px', marginBottom: '20px'}}>❌</div>
        <div style={{marginBottom: '20px'}}>
          Esperienza "{expParam}" non trovata
        </div>
        <button 
          onClick={() => history.push('/')}
          style={{
            padding: '12px 24px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          🏠 Torna alla Home
        </button>
      </div>
    )
  }
  


      // Esperienza semplice in iframe: solo back + iframe
      if (experienceType === 'iframe') {
        const src = (customParams && (customParams.url || customParams.iframeUrl)) || ''
        return (
          <React.Fragment>
            <FloatingBackButton />
            <div style={{
              position: 'absolute',
              inset: 0,
              background: '#000'
            }}>
              <iframe
                src={src}
                style={{ border: 'none', width: '100%', height: '100%' }}
                allow="fullscreen"
              />
            </div>
          </React.Fragment>
        )
      }
  

  // Estrai i parametri di animazione se presenti (con controlli di sicurezza)
  const animationParams = {}
  if (customParams) {
    // Copia i parametri di animazione se esistono
    if (customParams.animation) {
      animationParams.animation = customParams.animation
    }
    if (customParams.repeat) {
      animationParams.repeat = customParams.repeat
    }
    if (customParams.listAnim) {
      // Se listAnim è una stringa JSON, parsala
      if (typeof customParams.listAnim === 'string') {
        try {
          animationParams.listAnim = JSON.parse(customParams.listAnim)
        } catch (error) {
          console.error('❌ Errore nel parsing di listAnim:', error)
          animationParams.listAnim = [customParams.listAnim] // Fallback
        }
      } else {
        animationParams.listAnim = customParams.listAnim
      }
    }
    if (customParams.text) {
      animationParams.text = customParams.text
    }
  }

  // DEBUG DETTAGLIATO per parametri di animazione nel componente React
  console.log('🔍 DEBUG PARAMETRI ANIMAZIONE in Universal3DExperience:')
  console.log('  - customParams completo:', customParams)
  console.log('  - animationParams estratto:', animationParams)
  console.log('  - animation:', animationParams.animation)
  console.log('  - repeat:', animationParams.repeat)
  console.log('  - listAnim:', animationParams.listAnim)
  console.log('  - text:', animationParams.text)
  console.log('  - Tipo listAnim:', typeof customParams?.listAnim)
  console.log('  - È array?', Array.isArray(customParams?.listAnim))

  // Determina quale scena HTML usare
  let finalSceneHtml;
  
  if (expParam && sceneReady && sceneHtml) {
    // Se proviene da QR e la scena asincrona è pronta, usa quella
    finalSceneHtml = sceneHtml;
    console.log('🎯 Usando scena HTML asincrona con parametri WordPress');
  } else if (!expParam && sceneReady && sceneHtml) {
    // Se non proviene da QR (navigazione normale) e la scena è pronta, usa quella
    finalSceneHtml = sceneHtml;
    console.log('🎯 Usando scena HTML per navigazione normale (con verifica WordPress)');
  } else {
    // Se proviene da QR ma la scena asincrona non è ancora pronta, mostra loading
    finalSceneHtml = null;
  }
  
  // Ottieni le informazioni del modello
  const modelInfo = getModelInfo(modelType)
  const finalTitle = title || modelInfo.title
  const finalDescription = description || modelInfo.description

  // Log dei parametri di animazione se presenti
  if (animationParams.animation === 'yes') {
    console.log(`🎭 Parametri animazione in Universal3DExperience:`, animationParams)
  }



  // Se stiamo aspettando la scena (sia per QR che per navigazione normale), mostra loading specifico
  if (showScene && !sceneReady) {
    return (
      <div style={{
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        fontSize: '18px'
      }}>
        <div style={{fontSize: '48px', marginBottom: '20px'}}>🎨</div>
        <div>Generazione scena 3D con parametri WordPress...</div>
        {expParam && <div style={{fontSize: '14px', marginTop: '10px', opacity: 0.7}}>ID: {expParam}</div>}
      </div>
    )
  }
  
  return showScene && finalSceneHtml ? (
    <React.Fragment>
      <FloatingBackButton />
      <AFrameScene sceneHtml={finalSceneHtml} />
    </React.Fragment>
  ) : null
})

export {Universal3DExperience} 